import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, type CSSProperties } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Login from './pages/Login';
import Landing from './pages/Landing';
import Home from './pages/Home';
import Experiences from './pages/ExperiencesNew';
import PromptsGeniusLoci from './pages/PromptsGeniusLoci';
import LoadingSpinner from './components/LoadingSpinner';
import InteractionsLegacy from './pages/InteractionsLegacy';
import OneriPubblicitari from './pages/OneriPubblicitari';
import Documentazione from './pages/Documentazione';
import CreatorRequests from './pages/CreatorRequests';
import Analytics from './pages/Analytics';
import RegisteredUsers from './pages/RegisteredUsers';
import SuperadminUsersRegistry from './pages/SuperadminUsersRegistry';
import Events from './pages/Events';
import Notifications from './pages/Notifications';
import ProfileOrganization from './pages/ProfileOrganization';
import ApeConfigPage from './pages/ApeConfigPage';
import Audioguides from './pages/Audioguides';
import SuperadminCategories from './pages/SuperadminCategories';

const MainApp = () => {
  const { user, isAdmin, isSuperAdmin, loading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const canManageCreatorRequests = isSuperAdmin;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Login />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary mb-4">Accesso negato</h1>
          <p className="text-text-secondary mb-6">Permessi insufficienti per la console amministrativa.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <div
        className="flex flex-col flex-1 min-h-screen transition-all duration-300"
        style={{
          marginLeft: sidebarCollapsed ? '64px' : '288px',
          '--admin-sidebar-offset': sidebarCollapsed ? '64px' : '288px',
        } as CSSProperties}
      >
        <main className="flex-1 p-6">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Home />} />
            <Route path="/esperienze" element={<Experiences />} />
            <Route path="/interazioni" element={<InteractionsLegacy />} />
            <Route path="/genius-loci" element={<PromptsGeniusLoci />} />
            <Route path="/prompt" element={<PromptsGeniusLoci />} />
            <Route
              path="/ape-config"
              element={<Navigate to={canManageCreatorRequests ? '/superadmin/ape-config' : '/dashboard'} replace />}
            />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/oneri" element={<OneriPubblicitari />} />
            <Route path="/events" element={<Events />} />
            <Route path="/audioguide" element={<Audioguides />} />
            <Route path="/profilo" element={<ProfileOrganization />} />
            <Route
              path="/superadmin/organizzazioni-amministratori"
              element={canManageCreatorRequests ? <ProfileOrganization /> : <Navigate to="/dashboard" replace />}
            />
            <Route path="/creator-requests" element={canManageCreatorRequests ? <CreatorRequests /> : <Navigate to="/dashboard" replace />} />
            <Route path="/utenti-iscritti" element={canManageCreatorRequests ? <RegisteredUsers /> : <Navigate to="/dashboard" replace />} />
            <Route
              path="/superadmin/anagrafica-utenti"
              element={canManageCreatorRequests ? <SuperadminUsersRegistry /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="/superadmin/notifiche"
              element={canManageCreatorRequests ? <Notifications /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="/superadmin/ape-config"
              element={canManageCreatorRequests ? <ApeConfigPage /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="/superadmin/categorie"
              element={canManageCreatorRequests ? <SuperadminCategories /> : <Navigate to="/dashboard" replace />}
            />
            <Route path="/documentazione" element={<Documentazione />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </div>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/creator-signup" element={<Navigate to="/landing" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/*" element={<MainApp />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
